import { useState } from "react";
import { Ban, Mail, Pencil, Phone, Plus, Trash2, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fromNow } from "@/lib/time";
import { Btn } from "@/components/sales/ui";
import { CONTACT_ROLES, PHONE_FLAGS, PHONE_QUALITY } from "@/components/sales/constants";

const EMPTY = { name: "", title: "", phone: "", email: "", role: "other", preferred_method: "phone", status: "active", notes: "" };

const ContactDialog = ({ cid, contact, onClose }) => {
  const { run } = useApp();
  const [f, setF] = useState(contact ? { ...EMPTY, ...contact, phone: contact.phone?.raw || "" } : EMPTY);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] || EMPTY[k]]));
    if (await run(() => (contact?.id ? api.patch(`/sales/contacts/${contact.id}`, body) : api.post(`/sales/companies/${cid}/contacts`, body)), "Contact saved")) onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" aria-describedby={undefined} data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{contact?.id ? "Edit contact" : "Add contact"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Input required autoFocus placeholder="Name" value={f.name} onChange={set("name")} className="col-span-2" data-testid="contact-name-input" />
          <Input placeholder="Job title" value={f.title} onChange={set("title")} data-testid="contact-title-input" />
          <select value={f.role} onChange={set("role")} className="h-10 rounded-md border bg-card px-2 text-sm" data-testid="contact-role-select">{CONTACT_ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select>
          <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" />
          <Input placeholder="Email" type="email" value={f.email} onChange={set("email")} data-testid="contact-email-input" />
          <select value={f.preferred_method} onChange={set("preferred_method")} className="h-10 rounded-md border bg-card px-2 text-sm"><option value="phone">Prefers phone</option><option value="email">Prefers email</option><option value="either">Either</option></select>
          <select value={f.status} onChange={set("status")} className="h-10 rounded-md border bg-card px-2 text-sm"><option value="active">Active</option><option value="left">Left company</option><option value="do_not_contact">Do not contact</option></select>
          <Input placeholder="Notes" value={f.notes} onChange={set("notes")} className="col-span-2" />
          <div className="col-span-2 flex justify-end gap-2"><Btn type="button" onClick={onClose}>Cancel</Btn><Btn variant="primary" type="submit" data-testid="contact-save-btn">Save</Btn></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ContactsPanel = ({ company, adding, setAdding }) => {
  const { run } = useApp();
  const [edit, setEdit] = useState(null);
  return (
    <section className="rounded-2xl border bg-card/70 p-5" data-testid="contacts-panel">
      <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-lg font-semibold">Key contacts</h2>
        <button onClick={() => setAdding(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="add-contact-btn"><Plus className="inline h-3 w-3" /> Add</button></div>
      {company.contacts.length === 0 && <p className="text-sm text-muted-foreground">No contacts yet.</p>}
      {company.contacts.map((c) => (
        <div key={c.id} className="group flex items-start gap-3 border-b py-2.5 last:border-0" data-testid={`contact-${c.id}`}>
          <UserRound className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-medium">{c.name} {c.status === "do_not_contact" && <Ban className="inline h-3 w-3 text-red-400" />}<span className="ml-1 text-xs text-muted-foreground">{c.title} · {CONTACT_ROLES.find((r) => r.key === c.role)?.label}</span></p>
            <p className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {c.phone?.display && <a href={`tel:${c.phone.e164 || c.phone.raw}`} className="hover:text-foreground"><Phone className="inline h-3 w-3" /> {c.phone.display}</a>}
              {c.email && <a href={`mailto:${c.email}`} className="hover:text-foreground"><Mail className="inline h-3 w-3" /> {c.email}</a>}
              <span>{c.last_conversation_at ? `Spoke ${fromNow(c.last_conversation_at)}` : "No conversation yet"}</span>
            </p>
          </div>
          <button onClick={() => setEdit(c)} className="opacity-50 group-hover:opacity-100"><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={() => run(() => api.delete(`/sales/contacts/${c.id}`), "Contact removed")} className="opacity-50 hover:text-red-400 group-hover:opacity-100" data-testid={`delete-contact-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {(adding || edit) && <ContactDialog cid={company.id} contact={edit} onClose={() => { setAdding(false); setEdit(null); }} />}
    </section>
  );
};

export const PhonesPanel = ({ company }) => {
  const { run } = useApp();
  const [raw, setRaw] = useState("");
  const flag = (pid, f) => run(() => api.post(`/sales/companies/${company.id}/phones/flag`, { phone_id: pid, flag: f || null }), "Number updated");
  const add = (e) => { e.preventDefault(); if (raw.trim()) run(() => api.post(`/sales/companies/${company.id}/phones`, { raw, label: "main" }), "Number added").then(() => setRaw("")); };
  return (
    <section className="rounded-2xl border bg-card/70 p-5" data-testid="phones-panel">
      <h2 className="mb-1 font-display text-lg font-semibold">Phone numbers</h2>
      <p className="mb-3 text-xs text-muted-foreground">A valid format doesn't prove a line is connected. Flag numbers once you know.</p>
      {company.phones.map((p) => {
        const q = PHONE_QUALITY[p.quality] || { label: "Unknown", color: "#94a3b8" };
        return (
          <div key={p.id} className="flex flex-wrap items-center gap-2 border-b py-2 text-sm last:border-0" data-testid={`phone-${p.id}`}>
            <a href={`tel:${p.e164 || p.raw}`} className="font-mono hover:text-cyan-300">{p.display || p.raw}</a>
            <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: `${q.color}1f`, color: q.color }}>{p.flag === "verified_working" ? "Verified working" : q.label}</span>
            {p.line_type && <span className="text-xs text-muted-foreground">{p.line_type}</span>}
            <select value={p.flag || ""} onChange={(e) => flag(p.id, e.target.value)} className="ml-auto h-7 rounded-full border bg-card px-2 text-xs" data-testid={`phone-flag-${p.id}`}>
              <option value="">No flag</option>{PHONE_FLAGS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
        );
      })}
      <form onSubmit={add} className="mt-3 flex gap-2"><Input value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="Add a number" className="h-9" data-testid="add-phone-input" /><Btn type="submit" className="h-9">Add</Btn></form>
    </section>
  );
};
